/**
 * MobileSamplesRow
 *
 * Mobile-optimized sample shifts row.
 * Replaces the desktop SamplesRow for mobile (isMobile === 2).
 * Chips can be pressed and dragged onto the grid to add a shift.
 * Pressing sets draggingItem in Redux; releasing over a grid cell triggers the drop
 * (handled by MobileScheduler's global pointerup listener).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import { setDraggingItem } from "../../planningSlice";
import {
  addFavoriteShiftSample,
  deleteFavoriteShiftSample,
} from "../../../../components/scheduler/shifts/sampleShiftsSlice";
import {
  addFavoriteLeaveSample,
  deleteFavoriteLeaveSample,
} from "../../../../components/scheduler/leave/leaveSampleSlice";
import { getUserPreferences } from "../../../../app/utils/helpers";
import { getSampleColor, getSampleLabel, formatMinutes } from "./utils/sampleHelpers";
import { SampleChip, DragGhost } from "./components";

export default function MobileSamplesRow() {
  const dispatch = useDispatch();

  const sampleShifts = useSelector((state) => state.sampleShifts.list);
  const sampleLeaves = useSelector((state) => state.sampleLeaves.list);
  const selectedSamplesView = useSelector((state) => state.planning.selectedSamplesView);
  const draggingItem = useSelector((state) => state.planning.draggingItem);
  const dynamicLeaves = useSelector((state) => state.sampleLeaves.dynamicLeaves);
  const userId = useSelector((state) => state.login.userId);

  const [ghostPos, setGhostPos] = useState(null);
  const ghostShiftRef = useRef(null);
  const draggingRef = useRef(false);

  const rowRef = useRef(null);
  const [sliderVal, setSliderVal] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);

  const samples = useMemo(() => {
    if (selectedSamplesView !== "SHIFTS") return [];
    let arr = [
      ...(sampleShifts || []),
      ...(sampleLeaves || []).filter((s) => s.visibility),
    ].filter((s) => s.id);
    const order = getUserPreferences(userId, "SHIFTS_SAMPLES_ORDER");
    if (order) {
      arr = arr.sort(
        (a, b) => order.indexOf(`${a.id}`) - order.indexOf(`${b.id}`)
      );
    }
    // Favorites float to the front (stable sort keeps the existing order within each group)
    arr = arr.sort(
      (a, b) => (b.is_favorite === true ? 1 : 0) - (a.is_favorite === true ? 1 : 0)
    );
    return arr;
  }, [sampleShifts, sampleLeaves, selectedSamplesView, userId]);

  const handleItemPointerDown = (e, shift) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    ghostShiftRef.current = shift;
    dispatch(setDraggingItem(shift));
    setGhostPos({ x: e.clientX, y: e.clientY });
  };

  const handleToggleFavorite = (shift, e) => {
    e.stopPropagation();
    // Shifts and leaves live in different slices — route by sampleType.
    if (shift.sampleType === "leave") {
      if (shift.is_favorite) {
        dispatch(deleteFavoriteLeaveSample({ id_sample_leave: shift.id }));
      } else {
        dispatch(addFavoriteLeaveSample({ id_sample_leave: shift.id }));
      }
    } else {
      if (shift.is_favorite) {
        dispatch(deleteFavoriteShiftSample({ id_sample_shift: shift.id }));
      } else {
        dispatch(addFavoriteShiftSample({ id_sample_shift: shift.id }));
      }
    }
  };

  // Measure maxScroll and sync slider with row scroll
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    const measure = () => setMaxScroll(Math.max(0, el.scrollWidth - el.clientWidth));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const onScroll = () => setSliderVal(el.scrollLeft);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", onScroll);
    };
  }, [samples]);

  useEffect(() => {
    if (!ghostPos) return;

    const handleMove = (e) => {
      setGhostPos({ x: e.clientX, y: e.clientY });
    };

    const handleUp = () => {
      // MobileScheduler handles the actual drop and clears draggingItem.
      // We just clean up the ghost here.
      setGhostPos(null);
      ghostShiftRef.current = null;
      draggingRef.current = false;
    };

    document.addEventListener("pointermove", handleMove, { passive: true });
    document.addEventListener("pointerup", handleUp);
    document.addEventListener("pointercancel", handleUp);

    return () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", handleUp);
      document.removeEventListener("pointercancel", handleUp);
    };
  }, [ghostPos]);

  if (!samples.length) return null;

  const ghostShift = ghostShiftRef.current;

  return (
    <>
      <Box
        ref={rowRef}
        sx={{
          display: "flex",
          alignItems: "center",
          overflowX: "auto",
          gap: 0.75,
          px: 1.5,
          pt: 0.75,
          pb: 0.5,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          WebkitOverflowScrolling: "touch",
          flexShrink: 0,
        }}
      >
        {samples.map((shift) => {
          const color = getSampleColor(shift);
          const label = getSampleLabel(shift, dynamicLeaves);
          const isActive = draggingItem && draggingItem.id === shift.id;
          const timeLabel =
            shift.timeFrom != null && shift.timeTo != null
              ? `${formatMinutes(shift.timeFrom)}-${formatMinutes(shift.timeTo)}`
              : "";
          const isLeave = shift.sampleType === "leave" || !!shift.id_sample_leave || (!shift.task && !shift.timeFrom);
          // Both sample shifts and sample leaves carry the is_favorite flag.
          const canFavorite = typeof shift.is_favorite === "boolean";

          return (
            <SampleChip
              key={(shift.sampleType || "shift") + "_" + shift.id}
              shift={shift}
              color={color}
              label={label}
              timeLabel={timeLabel}
              isActive={isActive}
              isLeave={isLeave}
              canFavorite={canFavorite}
              isFavorite={!!shift.is_favorite}
              onToggleFavorite={(e) => handleToggleFavorite(shift, e)}
              onPointerDown={(e) => handleItemPointerDown(e, shift)}
            />
          );
        })}
      </Box>

      {/* Horizontal scroll slider */}
      {maxScroll > 0 && (
        <Box sx={{ px: 1.5, pb: 0.75 }}>
          <input
            type="range"
            className="samples-slider"
            min={0}
            max={maxScroll}
            value={sliderVal}
            onChange={(e) => {
              const val = Number(e.target.value);
              setSliderVal(val);
              if (rowRef.current) rowRef.current.scrollLeft = val;
            }}
            style={{
              width: "100%",
              height: 20,
              appearance: "none",
              WebkitAppearance: "none",
              background: "transparent",
              cursor: "pointer",
              touchAction: "none",
            }}
          />
          <style>{`
            input[type=range].samples-slider::-webkit-slider-runnable-track {
              height: 6px;
              background: rgba(0,0,0,0.08);
              border-radius: 3px;
            }
            input[type=range].samples-slider::-webkit-slider-thumb {
              -webkit-appearance: none;
              width: 36px;
              height: 10px;
              border-radius: 5px;
              background: #FF9015;
              margin-top: -2px;
              cursor: grab;
            }
            input[type=range].samples-slider::-moz-range-track {
              height: 6px;
              background: rgba(0,0,0,0.08);
              border-radius: 3px;
            }
            input[type=range].samples-slider::-moz-range-thumb {
              width: 36px;
              height: 10px;
              border-radius: 5px;
              background: #FF9015;
              border: none;
              cursor: grab;
            }
          `}</style>
        </Box>
      )}

      <DragGhost ghostPos={ghostPos} ghostShift={ghostShift} dynamicLeaves={dynamicLeaves} />
    </>
  );
}
